import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const empty = { nome: "", descrizione: "", docenti_ids: [] };

export default function MateriePage() {
  const [materie, setMaterie] = useState([]);
  const [docenti, setDocenti] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [m, d] = await Promise.all([api.get("/materie"), api.get("/users?role=docente")]);
      setMaterie(m.data); setDocenti(d.data);
    } catch { toast.error("Errore caricamento materie"); }
  };
  useEffect(() => { load(); }, []);

  const toggleDoc = (f, setF, id) => setF({ ...f, docenti_ids: f.docenti_ids.includes(id) ? f.docenti_ids.filter(x => x !== id) : [...f.docenti_ids, id] });

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    try { await api.post("/materie", form); toast.success("Materia creata"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/materie/${edit.id}`, { nome: edit.nome, descrizione: edit.descrizione || "", docenti_ids: edit.docenti_ids || [] }); toast.success("Materia aggiornata"); setEdit(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare la materia?")) return; await api.delete(`/materie/${id}`); toast.success("Eliminata"); load(); };

  const docentiPicker = (f, setF) => (
    <div className="border border-[#E2E8F0] rounded-lg max-h-56 overflow-y-auto p-2 space-y-1">
      {docenti.map(d => (
        <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-[#FFF5EB] rounded cursor-pointer">
          <input type="checkbox" checked={f.docenti_ids.includes(d.id)} onChange={() => toggleDoc(f, setF, d.id)} data-testid={`materia-doc-${d.id}`}/>
          <span className="text-sm">{d.name}{d.cognome ? ` ${d.cognome}` : ""}</span>
        </label>
      ))}
      {docenti.length === 0 && <div className="text-xs text-stone-500 p-2">Nessun docente in anagrafica.</div>}
    </div>
  );

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Anagrafica Didattica</div>
          <h1 className="text-3xl font-bold text-stone-900">Materie</h1>
          <p className="text-stone-600 mt-1">Materie insegnabili e docenti abilitati · un docente può erogare più materie</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-materia-btn"><Plus className="w-4 h-4 mr-2"/>Nuova Materia</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova Materia</DialogTitle><DialogDescription>Definisci la materia e i docenti abilitati a erogarla.</DialogDescription></DialogHeader>
            <div className="grid gap-4">
              <div><Label>Nome materia</Label><Input data-testid="materia-nome-input" value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/></div>
              <div><Label>Descrizione</Label><Textarea value={form.descrizione} onChange={e=>setForm({...form,descrizione:e.target.value})}/></div>
              <div><Label>Docenti abilitati</Label>{docentiPicker(form, setForm)}</div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="materia-save-btn">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 max-w-sm">
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca materia..." data-testid="materie-search"/>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {materie.filter(m=>!q || (m.nome||"").toLowerCase().includes(q.toLowerCase())).map(m => (
          <Card key={m.id} className="p-5 bg-white border-[#E2E8F0]" data-testid={`materia-card-${m.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#F7941E]"/><span className="font-bold text-stone-900 text-lg">{m.nome}</span></div>
              <div>
                <Button variant="ghost" size="sm" onClick={()=>setEdit({...m, docenti_ids: m.docenti_ids||[]})} data-testid={`edit-materia-${m.id}`}><Pencil className="w-4 h-4 text-[#F7941E]"/></Button>
                <Button variant="ghost" size="sm" onClick={()=>del(m.id)} data-testid={`del-materia-${m.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
              </div>
            </div>
            {m.descrizione && <div className="text-sm text-stone-600 mb-3">{m.descrizione}</div>}
            <div className="flex flex-wrap gap-1">
              {(m.docenti_nomi || []).length > 0 ? m.docenti_nomi.map((n,i)=><Badge key={`${m.id}-${n}-${i}`} className="bg-[#FFF5EB] text-[#C96F0E] border-[#F5D9B8]">{n}</Badge>) : <span className="text-xs text-stone-500">Nessun docente assegnato</span>}
            </div>
          </Card>
        ))}
        {materie.length === 0 && <div className="col-span-full text-center text-stone-500 py-12">Nessuna materia. Creane una per associarla a docenti e corsi.</div>}
      </div>

      <Dialog open={!!edit} onOpenChange={(o)=>{if(!o) setEdit(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica materia</DialogTitle><DialogDescription>Aggiorna nome, descrizione e docenti abilitati.</DialogDescription></DialogHeader>
          {edit && (
            <div className="grid gap-4">
              <div><Label>Nome materia</Label><Input data-testid="edit-materia-nome" value={edit.nome} onChange={e=>setEdit({...edit,nome:e.target.value})}/></div>
              <div><Label>Descrizione</Label><Textarea value={edit.descrizione||""} onChange={e=>setEdit({...edit,descrizione:e.target.value})}/></div>
              <div><Label>Docenti abilitati</Label>{docentiPicker(edit, setEdit)}</div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="edit-materia-save">Salva modifiche</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
